import { Injectable, signal } from '@angular/core';

export const ROOT_SENTINEL = '__ROOT__';

@Injectable({ providedIn: 'root' })
export class OrgChartDragStateService {
  readonly nestTargetId = signal<string | null>(null);
  readonly isOverLibrary = signal(false);
  private scheduledAnimationFrame: number | null = null;
  private pendingCursor: {
    x: number;
    y: number;
    draggedMemberId: string;
  } | null = null;

  updateFromCursor(cursorX: number, cursorY: number, draggedMemberId: string): void {
    const elements = this.getElementsFromPoint(cursorX, cursorY);
    this.setDragStateFromElements(elements, draggedMemberId);
  }

  updateFromCursorOnNextFrame(cursorX: number, cursorY: number, draggedMemberId: string): void {
    this.pendingCursor = { x: cursorX, y: cursorY, draggedMemberId };
    if (this.scheduledAnimationFrame !== null) return;

    this.scheduledAnimationFrame = requestAnimationFrame(() => {
      this.scheduledAnimationFrame = null;
      const pending = this.pendingCursor;
      this.pendingCursor = null;
      if (!pending) return;
      this.updateFromCursor(pending.x, pending.y, pending.draggedMemberId);
    });
  }

  getTargetIdFromCursor(cursorX: number, cursorY: number, draggedMemberId?: string): string | null {
    return this.getTargetIdFromElements(
      this.getElementsFromPoint(cursorX, cursorY),
      draggedMemberId
    );
  }

  clear(): void {
    if (this.scheduledAnimationFrame !== null) {
      cancelAnimationFrame(this.scheduledAnimationFrame);
      this.scheduledAnimationFrame = null;
    }
    this.pendingCursor = null;
    this.nestTargetId.set(null);
    this.isOverLibrary.set(false);
  }

  getElementsFromPoint(cursorX: number, cursorY: number): Element[] {
    const dragArtifacts = Array.from(
      document.querySelectorAll<HTMLElement>('.cdk-drag-preview, .cdk-drag-placeholder')
    );
    const previousPointerEvents = dragArtifacts.map(element => element.style.pointerEvents);

    dragArtifacts.forEach(element => {
      element.style.pointerEvents = 'none';
    });

    try {
      return document.elementsFromPoint(cursorX, cursorY);
    } finally {
      dragArtifacts.forEach((element, index) => {
        element.style.pointerEvents = previousPointerEvents[index];
      });
    }
  }

  private getTargetIdFromElements(elements: Element[], draggedMemberId?: string): string | null {
    for (const el of elements) {
      if (el.closest('[data-root-card]')) {
        return ROOT_SENTINEL;
      }

      const card = el.closest('[data-member-id]') as HTMLElement | null;
      const id = card?.dataset?.['memberId'];
      if (id && id !== draggedMemberId) {
        return id;
      }

      const node = el.closest('.org-node') as HTMLElement | null;
      const ownerCard = node?.querySelector<HTMLElement>(':scope > [data-member-id]');
      const ownerId = ownerCard?.dataset?.['memberId'];
      if (ownerId && ownerId !== draggedMemberId) {
        return ownerId;
      }
    }

    return null;
  }

  private setDragStateFromElements(elements: Element[], draggedMemberId: string): void {
    const nextTargetId = this.getTargetIdFromElements(elements, draggedMemberId);
    const nextIsOverLibrary = elements.some(el => !!el.closest('[data-company-library]'));

    if (this.nestTargetId() !== nextTargetId) {
      this.nestTargetId.set(nextTargetId);
    }
    if (this.isOverLibrary() !== nextIsOverLibrary) {
      this.isOverLibrary.set(nextIsOverLibrary);
    }
  }
}
